import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import { SecureRoute, Security } from '@okta/okta-react';
import React, { Suspense } from 'react';
import { Route, Switch, useHistory } from 'react-router-dom';
import { isAUNZ, isEMEA, isUSCA } from './common/helpers';
import { getOauthConfig, OAuthConfig } from './config';

const App = () => {
  const MainScreen = React.lazy(() => import('./screens/mainScreen'));
  const SearchScreen = React.lazy(() => import(/* webpackChunkName: "search" */ './screens/helioSearchScreen'));
  const ProfileDetailsScreen = React.lazy(() => import(/* webpackChunkName: "details" */ './screens/helioProfileDetailsScreen'));
  const ProfileEditScreen = React.lazy(() => import(/* webpackChunkName: "edit" */ './screens/helioProfileEditScreen'));
  const ProfileCreateEditScreen = React.lazy(() => import(/* webpackChunkName: "create" */ './screens/helioProfileCreateEditScreen'));
  const Login = React.lazy(() => import(/* webpackChunkName: "login" */ './login/Login'));

  /* okta-react doesn't support render in SecureRoute */
  const WrappedHelioSearchScreen = () => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    if (window.Cypress) window.xprops = { ...window.xprops, ...JSON.parse(window.localStorage.getItem('xprops')!) };

    return <SearchScreen {...window.xprops} />;
  };
  const WrappedHelioProfileDetailsScreen = (props: any) => {
    return <ProfileDetailsScreen {...Object.assign({}, props, window.xprops)} />;
  };
  const WrappedHelioProfileEditScreen = (props: any) => {
    if (
      window.xprops &&
      window.xprops.countryCode &&
      (isAUNZ(window.xprops.countryCode) || isUSCA(window.xprops.countryCode) || isEMEA(window.xprops.countryCode))
    ) {
      return <ProfileCreateEditScreen {...Object.assign({}, props, window.xprops)} />;
    }
    return <ProfileEditScreen {...Object.assign({}, props, window.xprops)} />;
  };
  const WrappedHelioProfileCreateEditScreen = (props: any) => {
    return <ProfileCreateEditScreen {...Object.assign({}, props, window.xprops)} />;
  };
  const WrappedLogin = (props: any) => {
    return <Login {...window.xprops} oauthConfig={props.oauthConfig} />;
  };

  const history = useHistory();
  const customAuthHandler = (oauthConfig: OAuthConfig) => () => {
    oktaAuth.token
      .getWithoutPrompt(oauthConfig)
      .then((token) => {
        if (token.tokens.accessToken) oktaAuth.tokenManager.add('access', token.tokens.accessToken);
        if (token.tokens.idToken) oktaAuth.tokenManager.add('id', token.tokens.idToken);
        const tokens = {
          accessToken: token.tokens.accessToken,
          idToken: token.tokens.idToken,
        };

        window.localStorage.setItem('tokens', JSON.stringify(tokens));
        console.log('token retrieved');
      })
      .catch((reason) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (!window.Cypress) {
          console.log('rejected: ' + reason);
          history.push('/login');
        }
      });
  };
  const restoreOriginalUri = async (_oktaAuth: OktaAuth, originalUri: string) => {
    history.replace(toRelativeUrl(originalUri, window.location.origin));
  };

  const oauthConfig = getOauthConfig(window.location.origin.includes('-external'));

  const oktaAuth = new OktaAuth(oauthConfig);

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <Switch>
        <Security oktaAuth={oktaAuth} onAuthRequired={customAuthHandler(oauthConfig)} restoreOriginalUri={restoreOriginalUri}>
          <SecureRoute exact path="/" component={MainScreen} />
          <SecureRoute exact path="/helio/search" component={WrappedHelioSearchScreen} />
          <SecureRoute exact path="/helio/profile" component={WrappedHelioProfileDetailsScreen} />
          <SecureRoute exact path="/helio/profile/edit" component={WrappedHelioProfileEditScreen} />
          <SecureRoute exact path="/helio/profile/create" component={WrappedHelioProfileCreateEditScreen} />
          <Route path="/login" component={() => <WrappedLogin oauthConfig={oauthConfig} />} />
        </Security>
      </Switch>
    </Suspense>
  );
};

export default App;
